/**
 * Simülasyon ekranı.
 *
 * Akış: son yayınlanan plan açılır → kullanıcı düzenler (sırala, ekle, çıkar,
 * duruş yap) → taslak olarak kaydeder → Çalıştır ile modele değerlendirtir →
 * beğenirse Canlıya Al ile yayınlar. Düzenleme tamamen istemci durumunda;
 * sunucuya yalnız düğmelerle gidilir.
 *
 * Asma kilidi: asmaya girmiş satırlar (plan.js → hatDurumu) düzenlenemez.
 * Kullanıcı düzenlerken hat ilerlediği için bayraklar periyodik olarak ve
 * Canlıya Al'dan hemen önce tazelenir; sınır düzenlenmiş bir satıra
 * ilerlediyse o satır canlı hâline döndürülür ve kullanıcı uyarılır.
 *
 * İstek/yanıt sözleşmesi ve uç listesi `api.js`'te, plan dönüşümleri
 * `plan.js`'te. Metinler `tr.json` / `en.json` içinde `simulation` altında.
 */
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Select, Spin, Tag, Tooltip, message } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import i18n from "../../i18next";
import UserContext from "../../Context/UserContext";
import * as api from "./api";
import {
  ayniPlan,
  eksikSatirlar,
  hatDurumu,
  kilitliOnekiHizala,
  konumaTasi,
  malzemeSozlugu,
  planGovdesi,
  satirdan,
  tasi,
  yeniSatir,
  zamanlarDan,
} from "./plan";
import PlanTablosu from "./PlanTablosu";
import AnalizPaneli from "./AnalizPaneli";
import SatirModal from "./SatirModal";
import GecmisCekmecesi from "./GecmisCekmecesi";

/**
 * Canlıya Al düğmesi. Canlı planı değiştirdiği için gerektiğinde buradan
 * kapatılabilir; kapalıyken düğme pasif kalır ve yayın isteği hiç gitmez.
 */
const CANLIYA_ALMA_ACIK = true;

/**
 * Asma bayraklarının tazelenme aralığı. Hat 20 dakikada bir satır ilerliyor;
 * 60 sn, kilitlenen satırı kullanıcı üzerinde uzun süre çalışmadan yakalar.
 */
const HAT_TAZELEME_MS = 60 * 1000;

/** Uyarı bildirimleri okunabilsin diye biraz uzun kalır (sn). */
const UYARI_SURESI = 8;

/** Yeni taslak için önerilen ad: "Taslak 23.09 14:05" / "Draft 23.09 14:05". */
const onerilenAd = (t) => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const damga = `${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
  return t("simulation.suggestedName", { stamp: damga });
};

/** Taslak ya da geçmiş kaydındaki planı sıraya dizip düzenlenebilir yapar. */
const plandan = (plan) =>
  [...(plan ?? [])].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)).map(satirdan);

/** Konum listesi → "19:40, 20:00" (periyodu olmayan konum sıra no ile). */
const saatler = (konumlar, zamanlar) =>
  konumlar.map((i) => zamanlar[i]?.periyot || `#${i + 1}`).join(", ");

export default function SimulasyonEkrani() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const kullanici = user?.accountDetail?.username ?? user?.accountDetail?.name ?? "";

  /* --- Taban plan ve listeler ---------------------------------------- */
  const [ilkYukleme, setIlkYukleme] = useState(true);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [zamanlar, setZamanlar] = useState([]);
  const [orderBatchId, setOrderBatchId] = useState(null);
  const [canliOrderId, setCanliOrderId] = useState(null);
  const [govdeListe, setGovdeListe] = useState([]);
  const [govdeOnListe, setGovdeOnListe] = useState([]);

  /* --- Hattın durumu (asma kilidi) ----------------------------------- */
  // Canlı planın son hâli ve kaç satırının asmaya girdiği.
  const [canli, setCanli] = useState({ satirlar: [], kilit: 0, asmada: -1 });
  // Tazelemede canlı plan başka bir order'a geçtiyse onun numarası.
  const [canliDegisti, setCanliDegisti] = useState(null);

  /* --- Düzenlenen plan ------------------------------------------------ */
  const [satirlar, setSatirlar] = useState([]);
  // Son kaydedilen / yüklenen hâl — "kaydedilmemiş değişiklik" bununla ölçülür.
  const [referans, setReferans] = useState([]);

  /* --- Taslaklar ------------------------------------------------------ */
  const [taslakListe, setTaslakListe] = useState([]);
  const [aktifTaslak, setAktifTaslak] = useState(null); // { id, name }

  /* --- Analiz --------------------------------------------------------- */
  const [sonuc, setSonuc] = useState(null);
  const [sonucBasligi, setSonucBasligi] = useState("");
  // Analizin hangi plan için üretildiği — plan sonradan değişirse işaretler
  // bayatlar, kullanıcı uyarılır.
  const [sonucPlani, setSonucPlani] = useState(null);

  /* --- Arayüz --------------------------------------------------------- */
  // null | "kaydet" | "calistir" | "canli" | "sil" | "taslak"
  const [mesgul, setMesgul] = useState(null);
  const [satirPenceresi, setSatirPenceresi] = useState(null); // { mod, konum }
  const [adPenceresi, setAdPenceresi] = useState(null); // { ad }
  const [gecmisAcik, setGecmisAcik] = useState(false);

  /**
   * Periyodik tazeleme bir zamanlayıcıdan çalışıyor; o anki durumu render
   * kapanışından değil bu referanslardan okur, yoksa ilk render'daki boş
   * planla çalışırdı.
   */
  const satirlarRef = useRef(satirlar);
  const orderBatchIdRef = useRef(orderBatchId);
  const canliOrderIdRef = useRef(canliOrderId);
  const mesgulRef = useRef(mesgul);
  // Canlıya Al onay penceresi açıkken periyodik tazeleme durur: onay verilen
  // plan, gönderilen planla aynı kalsın (kontrol onOk içinde yapılıyor).
  const onayAcikRef = useRef(false);
  satirlarRef.current = satirlar;
  orderBatchIdRef.current = orderBatchId;
  canliOrderIdRef.current = canliOrderId;
  mesgulRef.current = mesgul;

  const govdeSozluk = useMemo(() => malzemeSozlugu(govdeListe, "body_color"), [govdeListe]);
  const govdeOnSozluk = useMemo(
    () => malzemeSozlugu(govdeOnListe, "front_body_color"),
    [govdeOnListe]
  );

  const degisti = !ayniPlan(satirlar, referans);
  const eksik = useMemo(() => eksikSatirlar(satirlar), [satirlar]);
  const eksikKume = useMemo(() => new Set(eksik), [eksik]);
  const govde = useMemo(() => planGovdesi(satirlar), [satirlar]);
  const sonucBayat = sonuc && sonucPlani !== JSON.stringify(govde);
  // Renk değişimi işaretleri yalnız analiz güncelken anlamlı.
  const renkSiralari = useMemo(
    () =>
      new Set(sonucBayat ? [] : (sonuc?.renk_degisimleri ?? []).map((r) => Number(r.sira))),
    [sonuc, sonucBayat]
  );

  /**
   * Kilit yalnız tablodaki plan canlı planla AYNI order'dan geliyorsa
   * uygulanır: başka order'ın sıraları başka periyotlara denk gelir, konuma
   * göre kilitlemek yanlış satırları kilitlerdi.
   */
  const ayniOrder = Boolean(orderBatchId) && orderBatchId === canliOrderId;
  const kilitSayisi = ayniOrder ? canli.kilit : 0;
  const asmadaKonum = ayniOrder ? canli.asmada : -1;

  /* ================================================================== */
  /* Yükleme                                                             */
  /* ================================================================== */

  /** Adım 1: son yayınlanan plan + kaynak order + asma durumu. */
  const tabanYukle = useCallback(async () => {
    const [ham, order] = await Promise.all([api.planSatirlari(), api.sonOrder()]);
    const satirlarHam = Array.isArray(ham) ? ham : [];
    const yeni = plandan(satirlarHam);
    const durum = hatDurumu(satirlarHam);
    setZamanlar(zamanlarDan(satirlarHam));
    setSatirlar(yeni);
    setReferans(yeni);
    setOrderBatchId(Number(order) || null);
    setCanliOrderId(Number(order) || null);
    setCanli({ satirlar: yeni, kilit: durum.kilit, asmada: durum.asmada });
    setCanliDegisti(null);
    setAktifTaslak(null);
  }, []);

  /**
   * İlk yükleme efektine ve zamanlayıcıya bağlı fonksiyonlar kancadaki `t`'yi
   * değil sabit `i18n.t`'yi kullanır: `t` dil değişince yenileniyor,
   * bağımlılık olsaydı efekt yeniden koşar ve düzenlenmekte olan plan
   * sıfırlanırdı.
   */
  const taslaklariYukle = useCallback(async () => {
    try {
      const liste = await api.taslaklar();
      setTaslakListe(Array.isArray(liste) ? liste : []);
    } catch (err) {
      message.error(api.hataMesaji(err, i18n.t("simulation.err.drafts")));
    }
  }, []);

  /**
   * Asma bayraklarını tazeler ve kilitli öneki canlı planla hizalar.
   * Sınır, kullanıcının düzenlediği bir satıra ilerlediyse o satır canlı
   * hâline döner ve uyarı gösterilir.
   *
   * @returns {{ batchDegisti?: number, degisen: number[] }}
   */
  const hatiTazele = useCallback(async () => {
    const ham = await api.planSatirlari();
    const liste = Array.isArray(ham) ? ham : [];
    const durum = hatDurumu(liste);

    // Canlı plan yeniden üretilmiş: sıralar artık başka periyotlara denk
    // geliyor, eski plana göre hizalamak yanlış olur. Kullanıcıya söylenir.
    if (durum.batchId && durum.batchId !== canliOrderIdRef.current) {
      setCanliDegisti(durum.batchId);
      return { batchDegisti: durum.batchId, degisen: [] };
    }

    const canliSatirlar = plandan(liste);
    setCanli({ satirlar: canliSatirlar, kilit: durum.kilit, asmada: durum.asmada });

    if (orderBatchIdRef.current !== canliOrderIdRef.current) return { degisen: [] };

    const { satirlar: hizali, degisen } = kilitliOnekiHizala(
      satirlarRef.current,
      canliSatirlar,
      durum.kilit
    );
    if (degisen.length) {
      setSatirlar(hizali);
      message.warning(
        i18n.t("simulation.lock.entered", {
          count: degisen.length,
          times: saatler(degisen, zamanlarDan(liste)),
        }),
        UYARI_SURESI
      );
    }
    return { degisen };
  }, []);

  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const [g, go] = await Promise.all([
          api.govdeMalzemeleri(),
          api.govdeOnMalzemeleri(),
          tabanYukle(),
          taslaklariYukle(),
        ]);
        if (iptal) return;
        setGovdeListe(Array.isArray(g) ? g : []);
        setGovdeOnListe(Array.isArray(go) ? go : []);
      } catch (err) {
        if (!iptal) setYuklemeHatasi(api.hataMesaji(err, i18n.t("simulation.err.planLoad")));
      } finally {
        if (!iptal) setIlkYukleme(false);
      }
    })();
    return () => {
      iptal = true;
    };
  }, [tabanYukle, taslaklariYukle]);

  // Hat ilerlerken bayraklar periyodik tazelenir. Sekme görünmüyorken, bir
  // işlem sürerken ya da canlıya alma onayı açıkken atlanır.
  useEffect(() => {
    const zamanlayici = setInterval(() => {
      if (document.hidden || mesgulRef.current || onayAcikRef.current) return;
      if (!canliOrderIdRef.current) return;
      // Hata sessiz geçilir: bir sonraki turda yeniden denenir, Canlıya Al
      // da göndermeden önce ayrıca tazeliyor.
      hatiTazele().catch(() => {});
    }, HAT_TAZELEME_MS);
    return () => clearInterval(zamanlayici);
  }, [hatiTazele]);

  /** Kaydedilmemiş değişiklik varsa kullanıcıya sorar. */
  const degisiklikVarsaSor = (devam) => {
    if (!degisti) return devam();
    Modal.confirm({
      className: "sim-modal",
      title: t("simulation.confirm.unsavedTitle"),
      icon: <ExclamationCircleOutlined />,
      content: t("simulation.confirm.unsavedContent"),
      okText: t("simulation.continue"),
      cancelText: t("simulation.cancel"),
      onOk: devam,
    });
  };

  /**
   * Taslak ya da geçmiş kaydından gelen plan canlıyla aynı order'dansa,
   * asmaya girmiş önek canlı hâline getirilir — kayıt, hat o satırlara
   * ulaşmadan önce yapılmış olabilir.
   */
  const yuklenenPlaniHizala = (yeni, kaynakOrder) => {
    if (kaynakOrder !== canliOrderId || !canli.kilit) return yeni;
    const { satirlar: hizali, degisen } = kilitliOnekiHizala(yeni, canli.satirlar, canli.kilit);
    if (degisen.length) {
      message.warning(
        t("simulation.lock.aligned", { count: degisen.length, times: saatler(degisen, zamanlar) }),
        UYARI_SURESI
      );
    }
    return hizali;
  };

  const canliPlanaDon = () =>
    degisiklikVarsaSor(async () => {
      setMesgul("taslak");
      try {
        await tabanYukle();
        message.success(t("simulation.msg.liveLoaded"));
      } catch (err) {
        message.error(api.hataMesaji(err, t("simulation.err.planLoad")));
      } finally {
        setMesgul(null);
      }
    });

  const taslakAc = (id) =>
    degisiklikVarsaSor(async () => {
      setMesgul("taslak");
      try {
        const kayit = await api.taslak(id);
        const kaynak = Number(kayit.source_order_batch_id) || canliOrderId;
        const yeni = plandan(kayit?.plan);
        // Referans kayıtlı hâl: hizalama bir şey değiştirdiyse "kaydedilmemiş
        // değişiklik" görünür — taslak artık hattın gerisinde kalmış demektir.
        setReferans(yeni);
        setSatirlar(yuklenenPlaniHizala(yeni, kaynak));
        setAktifTaslak({ id: kayit.id ?? id, name: kayit.name });
        setOrderBatchId(kaynak);
      } catch (err) {
        message.error(api.hataMesaji(err, t("simulation.err.draftOpen")));
      } finally {
        setMesgul(null);
      }
    });

  /** Geçmişteki bir çalıştırmanın planını tabloya alır. */
  const gecmistenYukle = (kayit) =>
    degisiklikVarsaSor(() => {
      const kaynak = Number(kayit.source_order_batch_id) || canliOrderId;
      const yeni = plandan(kayit.plan);
      setReferans(yeni);
      setSatirlar(yuklenenPlaniHizala(yeni, kaynak));
      setAktifTaslak(null);
      setOrderBatchId(kaynak);
      setSonuc(kayit.result ?? null);
      // Analiz kaydedilen plana ait; hizalama değiştirdiyse bayat görünür.
      setSonucPlani(JSON.stringify(planGovdesi(yeni)));
      setSonucBasligi(t("simulation.historyRunTitle", { id: kayit.id }));
    });

  /* ================================================================== */
  /* Düzenleme                                                           */
  /* ================================================================== */
  // Tablo kilitli satırlarda düğmeleri zaten göstermiyor; aşağıdaki
  // denetimler, düğmeye basılı kalırken sınırın ilerlediği yarışlar için.

  const kilitliMi = (i) => i < kilitSayisi;

  const satirKaydet = (degerler) => {
    const { mod, konum } = satirPenceresi;
    // Pencere açıkken satır asmaya girmiş olabilir.
    if (mod === "duzenle" && kilitliMi(konum)) {
      message.warning(t("simulation.lock.lockedTip"));
      setSatirPenceresi(null);
      return;
    }
    setSatirlar((onceki) => {
      if (mod === "duzenle") {
        return onceki.map((s, i) => (i === konum ? { ...s, ...yeniSatir(degerler), key: s.key } : s));
      }
      // Ekleme: verilen konumun ALTINA; konum yoksa sona. Kilitli bölgeye
      // eklenemez — en erken ilk açık konuma.
      const yeni = [...onceki];
      const hedef = Math.max(konum == null ? yeni.length : konum + 1, kilitSayisi);
      yeni.splice(hedef, 0, yeniSatir(degerler));
      return yeni;
    });
    setSatirPenceresi(null);
  };

  const durusDegis = (i) => {
    if (kilitliMi(i)) return;
    setSatirlar((onceki) =>
      onceki.map((s, j) => {
        if (j !== i) return s;
        // Duruştan çıkan satırın malzemesi yok; kullanıcı düzenleyerek seçer.
        if (s.durus) return { ...s, durus: false };
        return { ...s, durus: true, govde: null, govde_on: null, uretim: 0 };
      })
    );
  };

  const tasiGuvenli = (i, yon) => {
    if (kilitliMi(i) || kilitliMi(i + yon)) return;
    setSatirlar((o) => tasi(o, i, yon));
  };

  const siralaGuvenli = (kaynak, hedef) => {
    if (kilitliMi(kaynak) || kilitliMi(hedef)) return;
    setSatirlar((o) => konumaTasi(o, kaynak, hedef));
  };

  const silGuvenli = (i) => {
    if (kilitliMi(i)) return;
    setSatirlar((o) => o.filter((_, j) => j !== i));
  };

  const duzenleAc = (i) => {
    if (kilitliMi(i)) return;
    setSatirPenceresi({ mod: "duzenle", konum: i });
  };

  /* ================================================================== */
  /* Kaydet                                                              */
  /* ================================================================== */

  const yeniTaslakKaydet = async (ad) => {
    setMesgul("kaydet");
    try {
      const yanit = await api.taslakEkle(
        { name: ad, source_order_batch_id: orderBatchId, plan: govde },
        kullanici
      );
      setAktifTaslak({ id: yanit?.id, name: ad });
      setReferans(satirlar);
      setAdPenceresi(null);
      message.success(t("simulation.msg.draftSaved"));
      taslaklariYukle();
    } catch (err) {
      message.error(api.hataMesaji(err, t("simulation.err.draftSave")));
    } finally {
      setMesgul(null);
    }
  };

  const kaydet = async () => {
    if (!aktifTaslak) {
      setAdPenceresi({ ad: onerilenAd(t) });
      return;
    }
    setMesgul("kaydet");
    try {
      await api.taslakGuncelle(
        {
          id: aktifTaslak.id,
          name: aktifTaslak.name,
          source_order_batch_id: orderBatchId,
          plan: govde,
        },
        kullanici
      );
      setReferans(satirlar);
      message.success(t("simulation.msg.draftUpdated"));
      taslaklariYukle();
    } catch (err) {
      message.error(api.hataMesaji(err, t("simulation.err.draftUpdate")));
    } finally {
      setMesgul(null);
    }
  };

  const taslakSil = () => {
    if (!aktifTaslak) return;
    Modal.confirm({
      className: "sim-modal",
      title: t("simulation.confirm.deleteTitle", { name: aktifTaslak.name }),
      icon: <ExclamationCircleOutlined />,
      content: t("simulation.confirm.deleteContent"),
      okText: t("simulation.delete"),
      okButtonProps: { danger: true },
      cancelText: t("simulation.cancel"),
      onOk: async () => {
        setMesgul("sil");
        try {
          await api.taslakSil(aktifTaslak.id, kullanici);
          setAktifTaslak(null);
          message.success(t("simulation.msg.draftDeleted"));
          taslaklariYukle();
        } catch (err) {
          message.error(api.hataMesaji(err, t("simulation.err.draftDelete")));
        } finally {
          setMesgul(null);
        }
      },
    });
  };

  /* ================================================================== */
  /* Çalıştır / Canlıya Al                                               */
  /* ================================================================== */

  /**
   * Çalıştır ve Canlıya Al aynı gövdeyi gönderir. `draft_id` yalnız plan
   * kayıtlı taslakla BİREBİR aynıysa eklenir: taslak açılıp kaydedilmeden
   * değiştirildiyse sonuç geçmişte o taslağa bağlanırsa yanıltıcı olurdu.
   */
  const kosuGovdesi = () => ({
    run_type: "prod",
    order_batch_id: orderBatchId,
    plan: govde,
    ...(aktifTaslak?.id && !degisti ? { draft_id: aktifTaslak.id } : {}),
  });

  /** Eksik satır modele gitmeden durdurulur. */
  const planGecerliMi = () => {
    if (!satirlar.length) {
      message.warning(t("simulation.warn.emptyPlan"));
      return false;
    }
    if (eksik.length) {
      message.warning(
        t("simulation.warn.missingRows", {
          rows: `${eksik.slice(0, 8).join(", ")}${eksik.length > 8 ? "…" : ""}`,
        })
      );
      return false;
    }
    if (!orderBatchId) {
      message.warning(t("simulation.warn.noSource"));
      return false;
    }
    return true;
  };

  const calistir = async () => {
    if (mesgul || !planGecerliMi()) return;
    setMesgul("calistir");
    const g = kosuGovdesi();
    try {
      const yanit = await api.calistir(g, kullanici);
      setSonuc(yanit);
      setSonucPlani(JSON.stringify(g.plan));
      setSonucBasligi(t("simulation.lastRun"));
    } catch (err) {
      message.error(api.hataMesaji(err, t("simulation.err.run")));
    } finally {
      setMesgul(null);
    }
  };

  const canliyaAl = () => {
    if (!CANLIYA_ALMA_ACIK || mesgul || !planGecerliMi()) return;
    // Pencere açıkken periyodik tazeleme durur; tazeleme onaydan hemen sonra,
    // göndermeden önce yapılır. Böylece onaylanan plan = gönderilen plan.
    onayAcikRef.current = true;
    Modal.confirm({
      className: "sim-modal",
      title: t("simulation.confirm.publishTitle"),
      icon: <ExclamationCircleOutlined />,
      content: t("simulation.confirm.publishContent"),
      okText: t("simulation.publish"),
      okButtonProps: { danger: true },
      cancelText: t("simulation.cancel"),
      onCancel: () => {
        onayAcikRef.current = false;
      },
      onOk: async () => {
        setMesgul("canli");
        try {
          // Son kontrol: hat bu arada ilerlemiş olabilir. Durum alınamazsa
          // ya da bir satır kilitlendiyse gönderilmez — kullanıcı planı görüp
          // yeniden dener.
          let tazeleme;
          try {
            tazeleme = await hatiTazele();
          } catch (err) {
            message.error(api.hataMesaji(err, t("simulation.lock.refreshFailed")));
            return;
          }
          if (tazeleme.batchDegisti) {
            message.error(
              t("simulation.lock.publishLiveChanged", { id: tazeleme.batchDegisti }),
              UYARI_SURESI
            );
            return;
          }
          if (tazeleme.degisen.length) {
            message.warning(t("simulation.lock.publishAborted"), UYARI_SURESI);
            return;
          }

          const g = kosuGovdesi();
          const yanit = await api.canliyaAl(g, kullanici);
          setSonuc(yanit);
          setSonucBasligi(t("simulation.publishedPlan"));
          message.success(t("simulation.msg.published"));
          // Plan ekranları artık yeni planı gösteriyor; taban plan yeniden
          // çekilir ki tablo canlıdakiyle aynı olsun.
          try {
            await tabanYukle();
            setSonucPlani(JSON.stringify(g.plan));
          } catch (err) {
            message.warning(api.hataMesaji(err, t("simulation.warn.reloadAfterPublish")));
          }
        } catch (err) {
          message.error(api.hataMesaji(err, t("simulation.err.publish")));
        } finally {
          onayAcikRef.current = false;
          setMesgul(null);
        }
      },
    });
  };

  /* ================================================================== */
  /* Görünüm                                                             */
  /* ================================================================== */

  if (ilkYukleme) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3 text-content-muted">
        <Spin />
        <span className="text-[13px]">{t("simulation.loading")}</span>
      </div>
    );
  }

  if (yuklemeHatasi) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("simulation.loadErrorTitle")}
        description={yuklemeHatasi}
        action={
          <Button size="small" onClick={() => window.location.reload()}>
            {t("simulation.retry")}
          </Button>
        }
      />
    );
  }

  const islemSuruyor = Boolean(mesgul);
  const farkliPlan = orderBatchId && canliOrderId && orderBatchId !== canliOrderId;

  return (
    <div className="flex flex-col gap-3">
      {/* --- Başlık ve taslak seçimi --- */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="m-0 text-[18px] font-semibold text-content">{t("simulation.title")}</h1>
          <Tag>{t("simulation.sourcePlan", { id: orderBatchId ?? "–" })}</Tag>
          {aktifTaslak ? (
            <Tag color="gold">{t("simulation.draftTag", { name: aktifTaslak.name })}</Tag>
          ) : (
            <Tag color="green">{t("simulation.livePlanTag")}</Tag>
          )}
          {degisti && <Tag color="orange">{t("simulation.unsaved")}</Tag>}
          <span className="text-[12px] text-content-muted">
            {t("simulation.rowCount", { count: satirlar.length })}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select
            style={{ minWidth: 240 }}
            placeholder={t("simulation.openDraft")}
            value={aktifTaslak?.id ?? undefined}
            onChange={taslakAc}
            disabled={islemSuruyor}
            loading={mesgul === "taslak"}
            options={taslakListe.map((x) => ({
              value: x.id,
              label: x.name ?? t("simulation.draftFallback", { id: x.id }),
            }))}
            notFoundContent={t("simulation.noDrafts")}
            showSearch
            optionFilterProp="label"
          />
          <Tooltip title={t("simulation.reloadLiveTip")}>
            <Button onClick={canliPlanaDon} disabled={islemSuruyor}>
              {t("simulation.livePlan")}
            </Button>
          </Tooltip>
          <Button onClick={() => setGecmisAcik(true)}>{t("simulation.history")}</Button>
        </div>
      </div>

      {/* --- Eylemler --- */}
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-surface px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={() => setSatirPenceresi({ mod: "ekle", konum: null })}
            disabled={islemSuruyor}
          >
            {t("simulation.addRow")}
          </Button>
          <Button
            onClick={kaydet}
            loading={mesgul === "kaydet"}
            disabled={islemSuruyor && mesgul !== "kaydet"}
          >
            {aktifTaslak ? t("simulation.save") : t("simulation.saveAsDraft")}
          </Button>
          {aktifTaslak && (
            <Button
              onClick={() =>
                setAdPenceresi({ ad: t("simulation.copyName", { name: aktifTaslak.name }) })
              }
              disabled={islemSuruyor}
            >
              {t("simulation.saveAs")}
            </Button>
          )}
          {aktifTaslak && (
            <Button
              danger
              onClick={taslakSil}
              loading={mesgul === "sil"}
              disabled={islemSuruyor && mesgul !== "sil"}
            >
              {t("simulation.deleteDraft")}
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="primary"
            onClick={calistir}
            loading={mesgul === "calistir"}
            disabled={islemSuruyor && mesgul !== "calistir"}
          >
            {mesgul === "calistir" ? t("simulation.running") : t("simulation.run")}
          </Button>
          {/* Kapatmak için CANLIYA_ALMA_ACIK = false yeterli. */}
          <Tooltip title={CANLIYA_ALMA_ACIK ? null : t("simulation.publishDisabled")}>
            <Button
              danger
              onClick={canliyaAl}
              loading={mesgul === "canli"}
              disabled={!CANLIYA_ALMA_ACIK || (islemSuruyor && mesgul !== "canli")}
            >
              {t("simulation.publish")}
            </Button>
          </Tooltip>
        </div>
      </div>

      {canliDegisti && (
        <Alert type="warning" showIcon message={t("simulation.lock.liveChanged", { id: canliDegisti })} />
      )}
      {farkliPlan && (
        <Alert
          type="info"
          showIcon
          message={t("simulation.otherPlanNotice", { planId: orderBatchId, liveId: canliOrderId })}
        />
      )}
      {eksik.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={t("simulation.missingRowsNotice", { count: eksik.length })}
        />
      )}

      {/* --- Plan ve analiz --- */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
        <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-line bg-surface">
          <PlanTablosu
            satirlar={satirlar}
            zamanlar={zamanlar}
            govdeSozluk={govdeSozluk}
            govdeOnSozluk={govdeOnSozluk}
            renkSiralari={renkSiralari}
            eksikler={eksikKume}
            kilitSayisi={kilitSayisi}
            asmadaKonum={asmadaKonum}
            mesgul={islemSuruyor}
            onSirala={siralaGuvenli}
            onTasi={tasiGuvenli}
            onDuzenle={duzenleAc}
            onArayaEkle={(i) => setSatirPenceresi({ mod: "ekle", konum: i })}
            onDurusDegis={durusDegis}
            onSil={silGuvenli}
          />
        </div>

        <aside className="w-full shrink-0 rounded-lg border border-line bg-surface p-3 xl:sticky xl:top-0 xl:max-h-[calc(100vh-140px)] xl:w-[460px] xl:overflow-auto">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[14px] font-semibold text-content">
              {t("simulation.analysisTitle")}
            </span>
            {mesgul === "calistir" && <Spin size="small" />}
          </div>
          {sonucBayat && (
            <Alert
              className="mb-2"
              type="warning"
              showIcon
              message={t("simulation.staleAnalysis")}
            />
          )}
          <AnalizPaneli sonuc={sonuc} baslik={sonucBasligi} />
        </aside>
      </div>

      {/* --- Pencereler --- */}
      <SatirModal
        acik={Boolean(satirPenceresi)}
        baslik={
          satirPenceresi?.mod === "duzenle"
            ? t("simulation.editRowTitle", { n: satirPenceresi.konum + 1 })
            : t("simulation.addRow")
        }
        satir={satirPenceresi?.mod === "duzenle" ? satirlar[satirPenceresi.konum] : null}
        govdeListe={govdeListe}
        govdeOnListe={govdeOnListe}
        onKaydet={satirKaydet}
        onVazgec={() => setSatirPenceresi(null)}
      />

      <Modal
        open={Boolean(adPenceresi)}
        title={t("simulation.draftName")}
        className="sim-modal"
        width={440}
        centered
        okText={t("simulation.save")}
        cancelText={t("simulation.cancel")}
        confirmLoading={mesgul === "kaydet"}
        okButtonProps={{ disabled: !adPenceresi?.ad?.trim() }}
        onOk={() => yeniTaslakKaydet(adPenceresi.ad.trim())}
        onCancel={() => setAdPenceresi(null)}
        destroyOnClose
      >
        <Input
          autoFocus
          value={adPenceresi?.ad ?? ""}
          maxLength={120}
          onChange={(e) => setAdPenceresi({ ad: e.target.value })}
          onPressEnter={() => adPenceresi?.ad?.trim() && yeniTaslakKaydet(adPenceresi.ad.trim())}
        />
      </Modal>

      <GecmisCekmecesi
        acik={gecmisAcik}
        onKapat={() => setGecmisAcik(false)}
        onPlaniYukle={gecmistenYukle}
      />
    </div>
  );
}
